import { useState, useEffect } from "react";
import { Users, ShoppingBag, UserCircle, Flag, ShieldAlert, Star } from "lucide-react";
import { toast } from "sonner";
import { useResource, fmt, money, timeAgo, HubRow, SubHeader, StatTile, PillTabs, EmptyState, SearchInput, PersonRow } from "./Shared";
import { ConfirmButton, runAction, UserDialog } from "../AdminActions";
import { api } from "@/lib/api";

const HUB = [
  { key: "pipeline", icon: Users, title: "Consumer Pipeline", subtitle: "Signups → ID verification → verified customers" },
  { key: "orders", icon: ShoppingBag, title: "Orders", subtitle: "Every order on the platform, live" },
  { key: "customers", icon: UserCircle, title: "All Customers", subtitle: "Every customer account" },
  { key: "flagged", icon: Flag, title: "Flagged accounts", subtitle: "Accounts flagged by the community or the system" },
  { key: "complaints", icon: ShieldAlert, title: "Complaint Center", subtitle: "Customer complaints and their resolution" },
];

function ConsumerPipeline({ onBack }) {
  const { data, loading } = useResource("consumers-pipeline");
  const [stage, setStage] = useState("signups");
  const [detailId, setDetailId] = useState(null);
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const people = data.creators.filter((c) => c.stage === stage);
  const help = data.creators.filter((c) => c.needs_help);
  const stageMeta = data.stages.find((s) => s.key === stage);
  return (
    <div data-testid="consumer-pipeline">
      <SubHeader title="Consumer Pipeline" onBack={onBack} />
      <div className="grid grid-cols-3 gap-2.5">
        {data.stages.map((s) => (
          <StatTile key={s.key} n={data.counts[s.key] ?? 0} label={s.label} active={stage === s.key} testId={`cstage-${s.key}`} onClick={() => setStage(s.key)} />
        ))}
      </div>
      {stageMeta?.hint && <p className="mt-3 rounded-xl bg-black/[0.04] px-4 py-3 text-[13px] text-charcoal/60">{stageMeta.hint}</p>}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm font-bold text-charcoal">{people.length} people</span>
        <button onClick={() => setStage("needs_help")} data-testid="consumer-needs-help"
          className={`rounded-full border px-3 py-1.5 text-xs font-bold ${stage === "needs_help" ? "border-terracotta bg-terracotta text-white" : "border-terracotta/40 text-terracotta"}`}>
          Needs help ({help.length})
        </button>
      </div>
      <div className="mt-3 space-y-2.5">
        {(stage === "needs_help" ? help : people).length === 0 ? <EmptyState icon={Users} text="Nobody at this step" /> :
          (stage === "needs_help" ? help : people).map((c) => (
            <PersonRow key={c.user_id} name={c.name} sub={`${c.email}${c.city ? " · " + c.city : ""}`}
              note={c.id_status === "approved" ? "ID approved" : c.stage_label} right={timeAgo(c.created_at)}
              testId={`consumer-person-${c.user_id}`} onClick={() => setDetailId(c.user_id)} />
          ))}
      </div>
      <UserDialog userId={detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} onAction={() => { setDetailId(null); }} />
    </div>
  );
}

function Orders({ onBack }) {
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/app-admin/data/orders", { params: { scope: tab === "all" ? "all" : tab, q } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load orders"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q]);

  const counts = data?.counts || {};
  const rows = data?.orders || [];
  return (
    <div data-testid="orders-screen">
      <SubHeader title="Orders" subtitle="Every order on the platform, live" onBack={onBack} />
      <PillTabs value={tab} onChange={setTab} options={[
        { key: "active", label: "Active", count: counts.active },
        { key: "completed", label: "Completed", count: counts.completed },
        { key: "cancelled", label: "Cancelled", count: counts.cancelled },
        { key: "all", label: "All" },
      ]} />
      <div className="mt-3"><SearchInput value={q} onChange={setQ} placeholder="Search customer, creator or order #" testId="orders-search" /></div>
      <div className="mt-4 space-y-2.5">
        {loading ? <p className="py-10 text-center text-sm text-charcoal/40">Loading…</p> :
          rows.length === 0 ? <EmptyState icon={ShoppingBag} text="No orders here." /> :
          rows.map((o) => (
            <div key={o.order_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`order-row-${o.order_id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-charcoal/45">#{(o.order_id || "").replace("order_", "").slice(0, 6)} · {fmt(o.created_at)}</span>
                <span className="font-display text-lg font-bold text-charcoal">{money(o.total)}</span>
              </div>
              <p className="mt-1 text-sm font-bold text-charcoal">{o.customer_name} → {o.vendor_name}</p>
              <p className="mt-0.5 text-[13px] text-charcoal/60">{(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-charcoal/5 px-2.5 py-1 text-xs font-bold capitalize text-charcoal/70">{o.status}</span>
                <span className="rounded-full bg-charcoal/5 px-2.5 py-1 text-xs font-bold capitalize text-charcoal/70">{(o.payment_status || "").replace(/_/g, " ")}</span>
                <span className="rounded-full bg-charcoal/5 px-2.5 py-1 text-xs font-bold capitalize text-charcoal/70">{o.fulfillment}</span>
                <span className="flex-1" />
                <ConfirmButton label="Refund" confirmLabel="Refund now?" variant="danger" testId={`refund-${o.order_id}`}
                  onConfirm={async () => { await runAction("order_refund", o.order_id); toast.success("Refund issued"); load(); }} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function Customers({ onBack }) {
  const { data, loading, reload } = useResource("users");
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const customers = (data?.users || []).filter((u) => u.role === "customer")
    .filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="customers-screen">
      <SubHeader title="All Customers" subtitle="Every customer account" onBack={onBack} />
      <SearchInput value={q} onChange={setQ} placeholder="Search name or email" testId="customers-search" />
      <div className="mt-4 space-y-2.5">
        {loading ? <p className="py-10 text-center text-sm text-charcoal/40">Loading…</p> :
          customers.length === 0 ? <EmptyState icon={UserCircle} text="No customers found." /> :
          customers.map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-4" data-testid={`customer-row-${u.user_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-bold text-charcoal">{u.name} {u.suspended && <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">Suspended</span>}</p>
                <p className="mt-0.5 truncate text-[13px] text-charcoal/55">{u.email} · joined {timeAgo(u.created_at)}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => setDetailId(u.user_id)} data-testid={`view-customer-${u.user_id}`}
                  className="rounded-lg border border-black/10 px-3 py-1.5 text-xs font-bold text-charcoal/70 hover:bg-black/[0.03]">View</button>
                {u.suspended ? (
                  <ConfirmButton label="Reactivate" confirmLabel="Confirm?" variant="success" testId={`reactivate-${u.user_id}`}
                    onConfirm={async () => { await runAction("user_status", u.user_id, { action: "reactivate" }); toast.success("Account reactivated"); reload(); }} />
                ) : (
                  <ConfirmButton label="Suspend" confirmLabel="Confirm?" variant="danger" testId={`suspend-${u.user_id}`}
                    onConfirm={async () => { await runAction("user_status", u.user_id, { action: "suspend" }); toast.success("Account suspended"); reload(); }} />
                )}
              </div>
            </div>
          ))}
      </div>
      <UserDialog userId={detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} onAction={() => { setDetailId(null); reload(); }} />
    </div>
  );
}

function Flagged({ onBack }) {
  const { data, loading, reload } = useResource("flagged-accounts");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  return (
    <div data-testid="flagged-screen">
      <SubHeader title="Flagged accounts" subtitle="Accounts flagged by the community or the system" onBack={onBack} />
      <div className="space-y-2.5">
        {data.accounts.length === 0 ? <EmptyState icon={Flag} text="No flagged accounts." /> :
          data.accounts.map((a) => (
            <div key={a.user_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`flagged-row-${a.user_id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-bold text-charcoal">{a.name} <span className="text-xs font-semibold text-charcoal/45">({a.role})</span></p>
                  <p className="mt-0.5 text-[13px] text-charcoal/55">{a.email}</p>
                  <p className="mt-1 text-xs text-charcoal/50">{a.violations} violations · {a.confirmed_blocks} confirmed blocks</p>
                </div>
                {a.suspended ? (
                  <ConfirmButton label="Reactivate" confirmLabel="Confirm?" variant="success" testId={`reactivate-${a.user_id}`}
                    onConfirm={async () => { await runAction("user_status", a.user_id, { action: "reactivate" }); toast.success("Account reactivated"); reload(); }} />
                ) : (
                  <ConfirmButton label="Suspend" confirmLabel="Confirm?" variant="danger" testId={`suspend-${a.user_id}`}
                    onConfirm={async () => { await runAction("user_status", a.user_id, { action: "suspend" }); toast.success("Account suspended"); reload(); }} />
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

const COMPLAINT_NEXT = {
  open: ["investigating", "resolved", "dismissed"],
  investigating: ["resolved", "dismissed"],
};

function Complaints({ onBack }) {
  const { data, loading, reload } = useResource("complaints");
  if (loading || !data) return <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p>;
  const c = data.counts || {};
  return (
    <div data-testid="complaints-screen">
      <SubHeader title="Complaint Center" subtitle="Customer complaints and their resolution" onBack={onBack} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="complaint-counts">
        {[["Open", c.open, "bg-red-100 text-red-700"], ["Investigating", c.investigating, "bg-amber-100 text-amber-700"],
          ["Resolved", c.resolved, "bg-emerald-100 text-emerald-700"], ["Dismissed", c.dismissed, "bg-charcoal/5 text-charcoal/60"]].map(([l, n, tone]) => (
          <span key={l} className={`rounded-full px-3 py-1.5 text-xs font-bold ${tone}`} data-testid={`complaints-${l.toLowerCase()}`}>{l}: {n ?? 0}</span>
        ))}
      </div>
      <div className="space-y-2.5">
        {data.complaints.length === 0 ? <EmptyState icon={ShieldAlert} text="No complaints." /> :
          data.complaints.map((cmp) => (
            <div key={cmp.complaint_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`complaint-row-${cmp.complaint_id}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold capitalize text-charcoal">{(cmp.category || "").replace(/_/g, " ")}</p>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${cmp.status === "open" ? "bg-red-100 text-red-700" : cmp.status === "investigating" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{cmp.status}</span>
              </div>
              <p className="mt-1 text-[13px] text-charcoal/60">{cmp.customer_name} → {cmp.vendor_name} · {fmt(cmp.created_at)}</p>
              <p className="mt-1.5 text-sm text-charcoal/80">{cmp.description}</p>
              {(COMPLAINT_NEXT[cmp.status] || []).length > 0 && (
                <div className="mt-3 flex gap-2">
                  {COMPLAINT_NEXT[cmp.status].map((next) => (
                    <ConfirmButton key={next} label={next[0].toUpperCase() + next.slice(1)} confirmLabel="Confirm?" testId={`complaint-${next}-${cmp.complaint_id}`}
                      variant={next === "resolved" ? "success" : next === "investigating" ? "warn" : "dark"}
                      onConfirm={async () => { await runAction("complaint_status", cmp.complaint_id, { status: next }); toast.success(`Complaint ${next}`); reload(); }} />
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

export default function ConsumersTab({ screen, goScreen }) {
  const [inner, setInner] = useState(null);
  const active = screen || inner;
  const back = () => (screen ? goScreen(null) : setInner(null));
  const open = screen ? goScreen : setInner;

  if (active === "pipeline") return <ConsumerPipeline onBack={back} />;
  if (active === "orders") return <Orders onBack={back} />;
  if (active === "customers") return <Customers onBack={back} />;
  if (active === "flagged") return <Flagged onBack={back} />;
  if (active === "complaints") return <Complaints onBack={back} />;

  return (
    <div data-testid="consumers-tab">
      <h2 className="font-display text-2xl font-bold text-charcoal">Consumers</h2>
      <p className="mb-5 mt-0.5 text-sm text-charcoal/55">Everything about your customers</p>
      <div className="space-y-2.5">
        {HUB.map((h) => (
          <HubRow key={h.key} icon={h.icon} title={h.title} subtitle={h.subtitle} testId={`consumers-hub-${h.key}`} onClick={() => open(h.key)} />
        ))}
      </div>
    </div>
  );
}
